import React, { useState, useRef, useEffect } from 'react';
import ActionButtons from '../ActionButtons';
import { Button } from '../design';
import Icon from '../Icon';
import renderMarkdown from '../../utils/renderMarkdown';
import asArray from '../../utils/asArray';
import { loadSession } from '../../services/ragSessions';
import { readRagStream } from '../../services/ragStream';

const QUICK_QUESTIONS = {
  admin: [
    'What are the current active alerts?',
    'How many servers are offline?',
    'What security threats were detected recently?',
    'How to investigate a high CPU incident?',
  ],
  operator: [
    'What is the current system status?',
    'What do the alert severity levels mean?',
    'How do I acknowledge an alert?',
    'What dashboards are available?',
  ],
};

function stripActionBlocks(content) {
  return content.replace(/```brain-action\s*\n[\s\S]*?\n```/g, '').trim();
}

function formatActionResult(action, result) {
  if (!result) return null;

  if (action === 'list_incidents' && result.incidents) {
    if (result.incidents.length === 0) return 'No incidents found.';
    const lines = [`Found ${result.count} incident(s):\n`];
    result.incidents.forEach((inc, i) => {
      const sev = (inc.severity || '').toUpperCase();
      const sevIcon = sev === 'CRITICAL' ? '🔴' : sev === 'HIGH' ? '🟠' : sev === 'MEDIUM' ? '🟡' : '🟢';
      lines.push(`${i + 1}. ${sevIcon} [${sev}] ${inc.title}`);
      lines.push(`   Status: ${inc.status} | Server: ${inc.server_name || 'N/A'}`);
      lines.push(`   Created: ${inc.created_at ? new Date(inc.created_at).toLocaleString() : 'N/A'}`);
      lines.push(`   ID: ${inc.id}`);
    });
    return lines.join('\n');
  }

  if (action === 'get_incident' && result.id) {
    const lines = [];
    const sev = (result.severity || '').toUpperCase();
    lines.push(result.title);
    lines.push('─'.repeat(40));
    lines.push(`Severity: ${sev} | Status: ${result.status}`);
    lines.push(`Category: ${result.category || 'N/A'} | Detected by: ${result.detected_by || 'N/A'}`);
    lines.push(`Server: ${result.server_name || 'N/A'}`);
    lines.push(`Created: ${result.created_at ? new Date(result.created_at).toLocaleString() : 'N/A'}`);
    if (result.resolved_at) lines.push(`Resolved: ${new Date(result.resolved_at).toLocaleString()}`);
    if (result.description) lines.push(`\nDescription:\n${result.description}`);
    if (result.root_cause) lines.push(`\nRoot Cause:\n${result.root_cause}`);
    if (result.resolution_steps?.length > 0) {
      lines.push(`\nResolution Steps:`);
      result.resolution_steps.forEach((s, i) => lines.push(`  ${i + 1}. ${s}`));
    }
    if (result.ai_analysis && typeof result.ai_analysis === 'object' && !Array.isArray(result.ai_analysis) && Object.keys(result.ai_analysis).length > 0) {
      lines.push(`\nAI Analysis:`);
      if (result.ai_analysis.summary) lines.push(`  Summary: ${result.ai_analysis.summary}`);
      if (result.ai_analysis.root_cause) lines.push(`  Root Cause: ${result.ai_analysis.root_cause}`);
    }
    lines.push(`\nID: ${result.id}`);
    return lines.join('\n');
  }

  if (action === 'rescan_services' && result.services_found !== undefined) {
    const services = result.services || [];
    const lines = [`Rescanned ${result.server || result.hostname || 'server'}: ${result.services_found} service(s) refreshed.`];
    // Surface the services the "log pipeline" suggestions are actually about.
    const watched = services.filter((s) => /filebeat|kafka|rsyslog|vector|fluent/i.test(s.name || ''));
    if (watched.length > 0) {
      lines.push('');
      lines.push('Log pipeline services:');
      watched.forEach((s) => lines.push(`  • ${s.name}: ${s.status || 'unknown'}`));
    }
    return lines.join('\n');
  }

  return null;
}

export default function ChatCore({ mode = 'operator', isBrain = false, compact = false, resetSignal = 0, notice = null, sessionId = null, onSessionChange = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('');
  const [sources, setSources] = useState([]);
  const [executingActions, setExecutingActions] = useState({});
  const [actionResults, setActionResults] = useState({});
  const messagesEndRef = useRef(null);
  // Session whose turns are already in `messages`; guards against re-fetching the
  // conversation we just streamed (the backend returns the id on the first turn).
  const loadedSessionRef = useRef(null);

  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, stage]);

  useEffect(() => {
    if (resetSignal > 0) {
      loadedSessionRef.current = null;
      setMessages([]);
      setSources([]);
      setStage('');
      setActionResults({});
      setExecutingActions({});
      setInput('');
    }
  }, [resetSignal]);

  // Open a saved conversation: restore its turns and continue it.
  useEffect(() => {
    if (!sessionId || loadedSessionRef.current === sessionId) return undefined;
    let cancelled = false;
    setMessages([]);
    setSources([]);
    setStage('loading history');
    loadSession(sessionId)
      .then((data) => {
        if (cancelled) return;
        const restored = (data?.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          sources: m.sources || [],
        }));
        loadedSessionRef.current = sessionId;
        setMessages(restored);
        const lastAssistant = [...restored].reverse().find((m) => m.role === 'assistant');
        setSources(asArray(lastAssistant?.sources));
      })
      .catch(() => {
        if (cancelled) return;
        setMessages([{ role: 'system', content: 'Could not load that conversation.' }]);
      })
      .finally(() => {
        if (!cancelled) setStage('');
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (notice) {
      setMessages(prev => [...prev, { role: 'system', content: notice }]);
    }
  }, [notice]);

  const executeAction = async (msgIdx, action, actionIdx) => {
    const actionKey = `${msgIdx}-${action.action}-${actionIdx}`;
    setExecutingActions(prev => ({ ...prev, [actionKey]: true }));
    try {
      const res = await fetch('/api/rag/actions/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: action.action,
          params: action.params || {},
          body: action.body || {},
        }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        data = { success: false, error: res.status ? `Server error ${res.status}` : 'Invalid response' };
      }
      setActionResults(prev => ({ ...prev, [actionKey]: data }));
      if (data.success) {
        const formatted = formatActionResult(action.action, data.result);
        if (formatted) {
          setMessages(prev => [...prev, { role: 'assistant', content: formatted, isActionResult: true }]);
        } else {
          setMessages(prev => [...prev, { role: 'system', content: `Action "${action.label || action.action}" completed successfully.` }]);
        }
      } else {
        setMessages(prev => [...prev, { role: 'system', content: `Action "${action.label || action.action}" failed: ${data.error || 'Unknown error'}` }]);
      }
    } catch (err) {
      setActionResults(prev => ({ ...prev, [actionKey]: { success: false, error: err.message } }));
      setMessages(prev => [...prev, { role: 'system', content: `Action execution failed: ${err.message}` }]);
    } finally {
      setExecutingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const appendToLast = (chunk) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        next[next.length - 1] = { ...last, content: last.content + chunk };
      } else {
        next.push({ role: 'assistant', content: chunk, streaming: true });
      }
      return next;
    });
  };

  const finalizeStream = (extra = {}) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        next[next.length - 1] = { ...last, streaming: false, ...extra };
      }
      return next;
    });
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setSources([]);
    setStage('processing');
    const historyPayload = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }));
    const payload = { question: userMsg.content, mode, history: historyPayload };
    if (sessionId) payload.session_id = sessionId;

    try {
      // Streaming first: the answer starts arriving in ~1.5s and the connection
      // never goes silent long enough for Cloudflare to cut it (unlike the old
      // single-response call, which 504'd at 120s).
      const result = await readRagStream(payload, {
        onStage: (s) => setStage(s || 'processing'),
        onSources: (s) => setSources(asArray(s)),
        onDelta: (chunk) => appendToLast(chunk),
      });

      if (result.status === 'unavailable') {
        await sendLegacy(payload);
        setLoading(false);
        setStage('');
        return;
      }
      if (result.status === 'error') {
        reportBrainError(result.error);
      } else {
        finalizeStream();
        if (result.done?.sources) setSources(asArray(result.done.sources));
        adoptSession(result.done?.session_id);
      }
    } catch (err) {
      const msg = err.message.includes('fetch') || err.message.includes('network')
        ? 'Connection lost — please try again'
        : `Error: ${err.message}`;
      setMessages(prev => [...prev, { role: 'assistant', content: msg, error: true }]);
    }
    setLoading(false);
    setStage('');
  };

  // Record the id of the conversation the backend just saved, without re-fetching the
  // turns we already have in state.
  const adoptSession = (newSessionId) => {
    if (!newSessionId || newSessionId === loadedSessionRef.current) return;
    loadedSessionRef.current = newSessionId;
    onSessionChange?.(newSessionId);
  };

  const reportBrainError = (error) => {
    const text = String(error || '');
    const hint = /timeout/i.test(text)
      ? 'The answer timed out. Try narrowing the question or enabling concise mode.'
      : `Error: ${text}`;
    finalizeStream({ error: true });
    setMessages(prev => [...prev, { role: 'assistant', content: hint, error: true }]);
  };

  const sendLegacy = async (payload) => {
    const res = await fetch('/api/rag/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setStage('searching');
    if (!res.ok) {
      let errorMsg = `Server error (${res.status})`;
      try {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          errorMsg = data.detail || data.error || errorMsg;
        }
      } catch {}
      reportBrainError(errorMsg);
      return;
    }
    const data = await res.json();
    setStage('generating');
    if (data.error) {
      reportBrainError(data.error);
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      setSources(asArray(data.sources));
      adoptSession(data.session_id);
    }
  };

  const resetConversation = () => {
    loadedSessionRef.current = null;
    setMessages([]);
    setSources([]);
    setStage('');
    setActionResults({});
    setExecutingActions({});
    setInput('');
    onSessionChange?.(null);
  };

  const quickQuestions = QUICK_QUESTIONS[mode] || QUICK_QUESTIONS.admin;
  const bubbleStyle = (role, error) => ({
    // 880 leaves room for wide code/tables in a full-page chat while the floating widget
    // (compact) keeps its own narrow width with inner scrolling.
    maxWidth: compact ? '100%' : 880,
    minWidth: 0,
    borderRadius: compact ? 12 : 16,
    padding: compact ? '8px 12px' : '12px 20px',
    background: role === 'user'
      ? (mode === 'admin' ? 'var(--accent-1)' : '#10b981')
      : error
      ? 'rgba(248,113,113,0.10)'
      : 'var(--glass)',
    color: role === 'user' ? '#fff' : error ? 'var(--danger)' : 'var(--text-1)',
    border: role === 'user' ? 'none' : error ? '1px solid rgba(248,113,113,0.25)' : '1px solid var(--glass-border)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', marginBottom: compact ? 8 : 16, minHeight: 0 }}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center" style={{ height: '100%', textAlign: 'center', padding: compact ? '12px 4px' : 0 }}>
            <div style={{
              width: compact ? 44 : 64, height: compact ? 44 : 64, borderRadius: compact ? 12 : 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: compact ? 10 : 16,
              background: mode === 'admin' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'linear-gradient(135deg, #10b981, #14b8a6)',
            }}>
              <Icon name="message-square" size={compact ? 22 : 32} style={{ color: '#fff' }} />
            </div>
            {!compact && (
              <>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>
                  {isBrain ? 'Synoti Brain' : 'Synoti Assistant'}
                </h2>
                <p className="muted" style={{ fontSize: 13, maxWidth: 420, marginBottom: 24 }}>
                  {mode === 'admin'
                    ? 'Ask anything about monitoring, incidents, security, and troubleshooting. Answers are based on the indexed knowledge base and live system state.'
                    : 'Ask about system status, alerts, and basic guidance. Simple, clear explanations for operators.'}
                </p>
              </>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 8, width: '100%', maxWidth: compact ? '100%' : 560,
            }}>
              {quickQuestions.slice(0, compact ? 3 : 4).map(q => (
                <button key={q} onClick={() => setInput(q)}
                  style={{ textAlign: 'left', padding: compact ? '8px 10px' : '12px 16px', borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', fontSize: compact ? 12 : 13, color: 'var(--text-2)', cursor: 'pointer' }}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          msg.role === 'system' ? (
            <div key={i} className="flex" style={{ justifyContent: 'center' }}>
              <div style={{ padding: '6px 12px', borderRadius: 999, background: 'rgba(251,191,36,0.10)', color: 'var(--warning)', fontSize: 11.5, border: '1px solid rgba(251,191,36,0.25)' }}>{msg.content}</div>
            </div>
          ) : (
            <div key={i}>
              <div className="flex" style={{ justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={bubbleStyle(msg.role, msg.error)}>
                  {msg.role === 'assistant' && !msg.error && !msg.isActionResult ? (
                    <div style={{ fontSize: compact ? 12.5 : 13, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(stripActionBlocks(msg.content)) }} />
                  ) : (
                    <div style={{ fontSize: compact ? 12.5 : 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{msg.role === 'assistant' ? stripActionBlocks(msg.content) : msg.content}</div>
                  )}
                </div>
              </div>
              {msg.role === 'assistant' && !msg.error && isBrain && (
                <ActionButtons content={msg.content} msgIdx={i} onExecute={executeAction} executing={executingActions} results={actionResults} />
              )}
            </div>
          )
        ))}

        {loading && (
          <div className="flex" style={{ justifyContent: 'flex-start' }}>
            <style>{`@keyframes mbdot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }`}</style>
            <div style={{ background: 'var(--glass)', borderRadius: 16, padding: compact ? '8px 12px' : '12px 20px', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: compact ? 12 : 13, color: 'var(--text-2)' }}>
                {stage === 'searching' ? 'Searching knowledge base…' : stage === 'generating' ? 'Generating answer…' : 'Processing'}
              </span>
              <span style={{ display: 'inline-flex', gap: 3, alignItems: 'flex-end' }}>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: 4, height: 4, borderRadius: '50%', display: 'inline-block',
                      background: mode === 'admin' ? 'var(--accent-1)' : '#10b981',
                      animation: 'mbdot 1.2s infinite ease-in-out', animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {sources.length > 0 && !compact && (
        <div style={{ marginBottom: 16 }}>
          <h3 className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Sources</h3>
          <div className="flex flex-wrap gap-2">
            {sources.map((s, i) => (
              <span key={i} className="flex items-center gap-1" style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--glass-border)', fontSize: 12, color: 'var(--text-2)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: mode === 'admin' ? 'var(--accent-1)' : '#10b981' }} />
                {s.file.split('/').pop()}
                <span className="muted">({(s.score || 0).toFixed(2)})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-end gap-2" style={{ background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: compact ? 6 : 8 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={mode === 'admin' ? 'Ask about alerts, incidents, security...' : 'Ask about system status, alerts...'}
          rows={compact ? 1 : 1}
          style={{ flex: 1, resize: 'none', background: 'transparent', fontSize: compact ? 12.5 : 13, color: 'var(--text-1)', outline: 'none', padding: '4px 8px', maxHeight: 128, border: 'none' }}
        />
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" icon="refresh-cw" onClick={resetConversation} title="New Chat" />
        )}
        <Button variant="primary" size={compact ? 'sm' : 'md'} onClick={send} disabled={!input.trim() || loading}>
          {loading ? '...' : 'Send'}
        </Button>
      </div>
      {!compact && (
        <p className="muted" style={{ fontSize: 10, marginTop: 6, textAlign: 'center' }}>
          Answers are based on indexed knowledge and live system state. Enter to send · Shift+Enter for new line
        </p>
      )}
    </div>
  );
}
